import type { ImportedAssetFileDescriptor } from './hydrateImportedTopologySourceBrepRuntime'
import type { ImportedPartBodyBrepBinding } from '../loaders/types'

/**
 * 仍由 BRep payload 驱动的 body 绑定。
 */
type ImportedBrepBackedBodyBinding = ImportedPartBodyBrepBinding & {
  brepRef: NonNullable<ImportedPartBodyBrepBinding['brepRef']>
}

/**
 * 规整导入 BRep 几何对象 ID 片段。
 *
 * 设计说明：
 * Worker GeometryState 的 objectId 会进入特征命令与拓扑绑定，必须避免路径分隔符、
 * 空白与控制字符造成后续序列化歧义。
 *
 * @param value 原始片段。
 * @returns 可拼入 objectId 的稳定片段。
 */
function sanitizeImportedBrepGeometryObjectIdSegment(value: string): string {
  const normalized = value.trim().replace(/[^a-zA-Z0-9._:-]/g, '_')
  return normalized.length > 0 ? normalized : 'unknown'
}

/**
 * 解析导入资产的稳定作用域。
 *
 * @param file 当前导入资产文件描述。
 * @returns 文件级稳定作用域。
 */
function resolveImportedAssetScope(file: ImportedAssetFileDescriptor): string {
  return file.uuid && file.uuid.trim().length > 0 ? file.uuid : file.fileId
}

/**
 * 收集文件可能出现在 imported-brep 几何对象 ID 中的资产作用域。
 *
 * 设计说明：
 * 旧版数据可能直接使用原始 `fileId/uuid`，新版 `buildImportedBrepGeometryObjectId`
 * 会对作用域做一次安全规整。调用方做来源匹配时必须同时兼容两种身份，避免局部子树
 * 切换时把 root `.puppy` 中的 zip-entry 几何错误归到根节点。
 *
 * @param file 导入资产文件描述。
 * @returns 可用于匹配 imported-brep asset scope 的候选键。
 */
function collectImportedBrepAssetScopeCandidateKeys(
  file: ImportedAssetFileDescriptor,
): string[] {
  const rawKeys = [
    file.uuid,
    file.fileId,
  ]
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim())
    .filter((value) => value.length > 0)

  return Array.from(new Set([
    ...rawKeys,
    ...rawKeys.map((value) => sanitizeImportedBrepGeometryObjectIdSegment(value)),
  ]
    .filter((value) => value.length > 0)))
}

/**
 * 从 imported-brep 几何对象 ID 中解析来源资产作用域。
 *
 * @param geometryObjectId Worker GeometryState 中的几何对象 ID。
 * @returns `imported-brep:<assetScope>:body|brep:<payload>` 中的 assetScope；非 imported-brep 时返回 `null`。
 */
export function resolveImportedBrepGeometryObjectAssetScope(
  geometryObjectId: string,
): string | null {
  const prefix = 'imported-brep:'
  if (!geometryObjectId.startsWith(prefix)) {
    return null
  }

  const payload = geometryObjectId.slice(prefix.length)
  const markerIndexes = [
    payload.indexOf(':body:'),
    payload.indexOf(':brep:'),
  ].filter((index) => index >= 0)
  const markerIndex = markerIndexes.length > 0 ? Math.min(...markerIndexes) : -1
  if (markerIndex <= 0) {
    return null
  }

  return payload.slice(0, markerIndex)
}

/**
 * 判断 imported-brep 来源资产作用域是否来自指定文件。
 *
 * 设计说明：轻量 `.puppy` 包会把内部 GLB 写成 `<packageUuid>::zip-entry::<entryPath>`。
 * 局部切换一级节点时，ownership 必须能从这个作用域反推项目节点的 package file，
 * 否则旧 active BRep 会被误判为根装配共享对象，导致 Worker 拒绝提交。
 *
 * @param assetScope `resolveImportedBrepGeometryObjectAssetScope()` 解析出的资产作用域。
 * @param file 导入资产文件描述。
 * @returns 该资产作用域是否属于传入文件。
 */
export function isImportedBrepGeometryObjectAssetScopeFromFile(
  assetScope: string,
  file: ImportedAssetFileDescriptor,
): boolean {
  const normalizedAssetScope = assetScope.trim()
  if (!normalizedAssetScope) {
    return false
  }

  return collectImportedBrepAssetScopeCandidateKeys(file).some((candidateKey) => (
    normalizedAssetScope === candidateKey
    || normalizedAssetScope.startsWith(`${candidateKey}::zip-entry::`)
  ))
}

/**
 * 构建导入 BRep 的语义几何分组键。
 *
 * 设计说明：
 * 1. SolidWorks 语义下，同一个 body 可能被 GLB 拆成多个可见 mesh/primitive；
 * 2. 建模与 STEP 导出必须按 body 语义去重，不能按可见 mesh 数量导出；
 * 3. 缺少 bodyId 时才退回 BRep sha，避免无语义数据时错误合并不同实体。
 *
 * @param file 当前导入资产文件描述。
 * @param binding body 级 BRep 绑定。
 * @returns 语义几何分组键。
 */
export function buildImportedBrepGeometryGroupKey(
  file: ImportedAssetFileDescriptor,
  binding: ImportedBrepBackedBodyBinding,
): string {
  const bodyScope = binding.bodyId && binding.bodyId.trim().length > 0
    ? `body:${binding.bodyId.trim()}`
    : `brep:${binding.brepRef.sha256}`
  return `${resolveImportedAssetScope(file)}|${bodyScope}`
}

/**
 * 构建导入 BRep 在 Worker GeometryState 中的对象 ID。
 *
 * @param file 当前导入资产文件描述。
 * @param binding body 级 BRep 绑定。
 * @returns 可用于 GeometryState 的稳定 objectId。
 */
export function buildImportedBrepGeometryObjectId(
  file: ImportedAssetFileDescriptor,
  binding: ImportedBrepBackedBodyBinding,
): string {
  return [
    'imported-brep',
    sanitizeImportedBrepGeometryObjectIdSegment(resolveImportedAssetScope(file)),
    sanitizeImportedBrepGeometryObjectIdSegment(
      binding.bodyId && binding.bodyId.trim().length > 0
        ? `body:${binding.bodyId.trim()}`
        : `brep:${binding.brepRef.sha256}`,
    ),
  ].join(':')
}

/**
 * 构建导入 BRep 在 STEP 导出中的稳定几何定义 ID。
 *
 * 设计说明：
 * 1. Worker `objectId` 描述的是某次导入/某个 body 的运行时对象，不能作为 STEP 几何定义复用依据；
 * 2. BRep `sha256` 描述 payload 内容本身，同一份几何在不同 occurrence 或不同文件包装下应复用同一个 XCAF shape definition；
 * 3. 这里仅生成定义身份，不改变运行时对象 ID，避免影响特征命令对 live shape 的寻址。
 *
 * @param binding body 级 BRep 绑定。
 * @returns 可用于 STEP 导出定义复用的稳定 ID。
 */
export function buildImportedBrepExportDefinitionId(
  binding: ImportedBrepBackedBodyBinding,
): string {
  const segments = [
    'imported-brep-definition',
    sanitizeImportedBrepGeometryObjectIdSegment(binding.brepRef.sha256),
  ]
  if (binding.brepBodyOrdinal !== undefined) {
    if (!Number.isInteger(binding.brepBodyOrdinal) || binding.brepBodyOrdinal < 0) {
      throw new Error(
        `导入 BRep 定义 ID 的 Body 序号非法: ordinal=${binding.brepBodyOrdinal}`,
      )
    }
    segments.push(binding.brepBodyKind ?? 'solid', String(binding.brepBodyOrdinal))
  }
  return segments.join(':')
}
